import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ReactComponent as InfoTooltipIcon } from '@assets/images/Input/info-tooltip.svg'
import Badge from '../Badge/Badge.component'
import Checkbox from '../Checkbox/Checkbox.component'
import type {
  MultiselectComboboxOption,
  MultiselectComboboxProps,
} from './MultiselectCombobox.types'
import {
  MULTISELECT_COMBOBOX_DEMO_ERROR_MESSAGE,
  MULTISELECT_COMBOBOX_DEMO_INFO_MESSAGE,
  MULTISELECT_COMBOBOX_DEMO_LABEL,
  MULTISELECT_COMBOBOX_DEMO_OPTIONS,
  MULTISELECT_COMBOBOX_DEMO_PLACEHOLDER,
  MULTISELECT_COMBOBOX_DEMO_QUERY,
  MULTISELECT_COMBOBOX_DEMO_SELECTED_VALUES,
  MULTISELECT_COMBOBOX_EMPTY_MESSAGE,
} from './MultiselectCombobox.types'
import './MultiselectCombobox.css'

const filterOptions = (
  options: MultiselectComboboxOption[],
  query: string,
  filterable: boolean,
): MultiselectComboboxOption[] => {
  if (!filterable || !query.trim()) {
    return options
  }

  const normalizedQuery = query.trim().toLowerCase()

  return options.filter(
    (option) =>
      option.label.toLowerCase().includes(normalizedQuery) ||
      option.value.toLowerCase().includes(normalizedQuery),
  )
}

const MultiselectCombobox: React.FC<MultiselectComboboxProps> = ({
  label = MULTISELECT_COMBOBOX_DEMO_LABEL,
  placeholder = MULTISELECT_COMBOBOX_DEMO_PLACEHOLDER,
  options = MULTISELECT_COMBOBOX_DEMO_OPTIONS,
  value,
  defaultValue,
  maxSelected,
  maxVisibleSelected,
  filterable = true,
  closeOnSelect = false,
  disabled = false,
  error = false,
  helperText,
  optional = false,
  showTooltip = false,
  className = '',
  darkMode = false,
  previewState,
  onChange,
  onRemove,
}) => {
  const inputId = useId()
  const listboxId = useId()
  const fieldRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [internalValues, setInternalValues] = useState<string[]>(
    defaultValue ?? [],
  )
  const [query, setQuery] = useState('')
  const [menuOpen, setMenuOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)

  const isPreview = previewState != null
  const isDisabled = disabled || previewState === 'disabled'
  const isError = error || previewState === 'error'
  const isOptional = optional || previewState === 'optional'
  const hasTooltip = showTooltip || previewState === 'with-tooltip'

  const selectedValues = useMemo(() => {
    if (isPreview) {
      if (
        previewState === 'filled' ||
        previewState === 'disabled' ||
        previewState === 'error' ||
        previewState === 'with-info' ||
        previewState === 'expanded'
      ) {
        return MULTISELECT_COMBOBOX_DEMO_SELECTED_VALUES
      }

      return []
    }

    return value ?? internalValues
  }, [internalValues, isPreview, previewState, value])

  const resolvedQuery = useMemo(() => {
    if (isPreview) {
      return previewState === 'expanded' ? MULTISELECT_COMBOBOX_DEMO_QUERY : ''
    }

    return query
  }, [isPreview, previewState, query])

  const selectedOptions = useMemo(
    () =>
      selectedValues.map(
        (selectedValue) =>
          options.find((option) => option.value === selectedValue) ?? {
            value: selectedValue,
            label: selectedValue,
          },
      ),
    [options, selectedValues],
  )

  const resolvedMaxVisible = useMemo(() => {
    if (maxVisibleSelected != null) {
      return maxVisibleSelected
    }

    // filled/overflow chrome shows two chips + ellipsis.
    if (
      isPreview &&
      (previewState === 'filled' ||
        previewState === 'disabled' ||
        previewState === 'error' ||
        previewState === 'with-info')
    ) {
      return 2
    }

    return undefined
  }, [isPreview, maxVisibleSelected, previewState])

  const visibleSelectedOptions = useMemo(() => {
    if (
      resolvedMaxVisible == null ||
      selectedOptions.length <= resolvedMaxVisible
    ) {
      return selectedOptions
    }

    return selectedOptions.slice(0, resolvedMaxVisible)
  }, [resolvedMaxVisible, selectedOptions])

  const hiddenSelectedCount =
    selectedOptions.length - visibleSelectedOptions.length

  const resolvedHelperText = useMemo(() => {
    if (helperText != null) {
      return helperText
    }

    if (previewState === 'error') {
      return MULTISELECT_COMBOBOX_DEMO_ERROR_MESSAGE
    }

    if (previewState === 'with-info') {
      return MULTISELECT_COMBOBOX_DEMO_INFO_MESSAGE
    }

    return undefined
  }, [helperText, previewState])

  const displayOptions = useMemo(
    () => filterOptions(options, resolvedQuery, filterable),
    [filterable, options, resolvedQuery],
  )

  const isMenuVisible =
    isPreview && previewState === 'expanded' ? true : menuOpen

  const isOpenTrigger =
    isMenuVisible && (previewState === 'expanded' || (!isPreview && menuOpen))

  const isLimitReached =
    maxSelected != null && selectedValues.length >= maxSelected

  const emitChange = useCallback(
    (nextValues: string[]) => {
      if (value === undefined) {
        setInternalValues(nextValues)
      }

      onChange?.(nextValues)
    },
    [onChange, value],
  )

  const openMenu = useCallback(() => {
    if (isPreview || isDisabled) {
      return
    }

    setMenuOpen(true)
  }, [isDisabled, isPreview])

  const closeMenu = useCallback(() => {
    setMenuOpen(false)
    setActiveIndex(-1)
  }, [])

  const handleToggleMenu = useCallback(() => {
    if (isPreview || isDisabled) {
      return
    }

    setMenuOpen((open) => {
      if (open) {
        setActiveIndex(-1)
        return false
      }

      return true
    })
    inputRef.current?.focus()
  }, [isDisabled, isPreview])

  const removeValue = useCallback(
    (optionValue: string) => {
      if (isPreview || isDisabled) {
        return
      }

      emitChange(selectedValues.filter((item) => item !== optionValue))
      onRemove?.(optionValue)
    },
    [emitChange, isDisabled, isPreview, onRemove, selectedValues],
  )

  const handleToggleOption = useCallback(
    (optionValue: string) => {
      if (isPreview || isDisabled) {
        return
      }

      if (selectedValues.includes(optionValue)) {
        removeValue(optionValue)
      } else {
        if (isLimitReached) {
          return
        }

        emitChange([...selectedValues, optionValue])
      }

      setQuery('')
      setActiveIndex(-1)

      if (closeOnSelect) {
        closeMenu()
      }

      inputRef.current?.focus()
    },
    [
      closeMenu,
      closeOnSelect,
      emitChange,
      isDisabled,
      isLimitReached,
      isPreview,
      removeValue,
      selectedValues,
    ],
  )

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (isPreview || isDisabled) {
        return
      }

      setQuery(event.target.value)
      setMenuOpen(true)
      setActiveIndex(-1)
    },
    [isDisabled, isPreview],
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (isPreview || isDisabled) {
        return
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        if (!menuOpen) {
          setMenuOpen(true)
          setActiveIndex(0)
          return
        }

        setActiveIndex((index) =>
          displayOptions.length === 0
            ? -1
            : Math.min(index + 1, displayOptions.length - 1),
        )
        return
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault()
        if (!menuOpen) {
          setMenuOpen(true)
          setActiveIndex(displayOptions.length - 1)
          return
        }

        setActiveIndex((index) => Math.max(index - 1, 0))
        return
      }

      if (event.key === 'Enter' && menuOpen && activeIndex >= 0) {
        event.preventDefault()
        const option = displayOptions[activeIndex]
        if (option) {
          handleToggleOption(option.value)
        }
        return
      }

      if (
        event.key === 'Backspace' &&
        query.length === 0 &&
        selectedValues.length > 0
      ) {
        event.preventDefault()
        removeValue(selectedValues[selectedValues.length - 1])
        return
      }

      if (event.key === 'Escape' && menuOpen) {
        event.preventDefault()
        closeMenu()
      }
    },
    [
      activeIndex,
      closeMenu,
      displayOptions,
      handleToggleOption,
      isDisabled,
      isPreview,
      menuOpen,
      query,
      removeValue,
      selectedValues,
    ],
  )

  useEffect(() => {
    if (isPreview || !menuOpen) {
      return undefined
    }

    const handlePointerDown = (event: MouseEvent): void => {
      if (
        fieldRef.current &&
        !fieldRef.current.contains(event.target as Node)
      ) {
        closeMenu()
      }
    }

    document.addEventListener('mousedown', handlePointerDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
    }
  }, [closeMenu, isPreview, menuOpen])

  const classNames = [
    'ds-multiselect-combobox',
    isDisabled ? 'ds-multiselect-combobox--disabled' : '',
    isError ? 'ds-multiselect-combobox--error' : '',
    darkMode ? 'ds-multiselect-combobox--dark' : '',
    isPreview ? 'ds-multiselect-combobox--preview' : '',
    previewState ? `ds-multiselect-combobox--preview-${previewState}` : '',
    selectedValues.length > 0 ? 'ds-multiselect-combobox--filled' : '',
    isOpenTrigger ? 'ds-multiselect-combobox--open' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={classNames}>
      <div className="ds-multiselect-combobox__label-row">
        <label className="ds-multiselect-combobox__label" htmlFor={inputId}>
          {label}
          {isOptional ? (
            <span className="ds-multiselect-combobox__optional">
              {' '}
              (Optional)
            </span>
          ) : null}
        </label>
        {hasTooltip ? (
          <span
            className="ds-multiselect-combobox__tooltip-trigger"
            title={label}
            aria-label={`More information about ${label}`}
          >
            <InfoTooltipIcon />
          </span>
        ) : null}
      </div>

      <div
        className={`ds-multiselect-combobox__field${
          previewState === 'focused'
            ? ' ds-multiselect-combobox__field--focused-preview'
            : ''
        }`}
        ref={fieldRef}
      >
        <div
          className="ds-multiselect-combobox__control"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              event.preventDefault()
              inputRef.current?.focus()
              openMenu()
            }
          }}
        >
          <div className="ds-multiselect-combobox__chips">
            {visibleSelectedOptions.map((option) => (
              <Badge
                key={option.value}
                label={option.chipLabel ?? option.label}
                mode={darkMode ? 'solid' : 'tinted'}
                color="blue"
                dismissible
                onDismiss={() => removeValue(option.value)}
              />
            ))}
            {hiddenSelectedCount > 0 ? (
              <span
                className="ds-multiselect-combobox__overflow"
                aria-label={`${hiddenSelectedCount} more selected`}
              >
                ...
              </span>
            ) : null}
            <input
              ref={inputRef}
              id={inputId}
              type="text"
              className="ds-multiselect-combobox__input"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={isMenuVisible}
              aria-controls={listboxId}
              aria-activedescendant={
                activeIndex >= 0
                  ? `${listboxId}-option-${activeIndex}`
                  : undefined
              }
              placeholder={selectedOptions.length === 0 ? placeholder : ''}
              value={resolvedQuery}
              disabled={isPreview || isDisabled}
              tabIndex={isPreview ? -1 : 0}
              autoComplete="off"
              onChange={handleInputChange}
              onFocus={openMenu}
              onKeyDown={handleKeyDown}
            />
          </div>
          <button
            type="button"
            className="ds-multiselect-combobox__toggle"
            tabIndex={isPreview ? -1 : 0}
            aria-label={isMenuVisible ? 'Close options' : 'Open options'}
            aria-expanded={isMenuVisible}
            aria-controls={listboxId}
            disabled={isPreview || isDisabled}
            onMouseDown={(event) => {
              event.preventDefault()
            }}
            onClick={handleToggleMenu}
          >
            <i
              className="mgc mgc_down_small_fill ds-multiselect-combobox__chevron"
              aria-hidden="true"
            />
          </button>
        </div>

        {isMenuVisible ? (
          <div
            id={listboxId}
            className="ds-multiselect-combobox__menu"
            role="listbox"
            aria-multiselectable="true"
          >
            <div className="ds-multiselect-combobox__menu-scroll">
              {displayOptions.length === 0 ? (
                <p className="ds-multiselect-combobox__empty">
                  {MULTISELECT_COMBOBOX_EMPTY_MESSAGE}
                </p>
              ) : (
                displayOptions.map((option, index) => {
                  const isSelected = selectedValues.includes(option.value)
                  const isActive = index === activeIndex
                  const isOptionDisabled =
                    isPreview || isDisabled || (isLimitReached && !isSelected)

                  return (
                    <div
                      key={option.value}
                      id={`${listboxId}-option-${index}`}
                      className={`ds-multiselect-combobox__option${
                        isActive
                          ? ' ds-multiselect-combobox__option--active'
                          : ''
                      }`}
                      role="option"
                      aria-selected={isSelected}
                      onMouseDown={(event) => {
                        event.preventDefault()
                      }}
                      onMouseEnter={() => setActiveIndex(index)}
                    >
                      <Checkbox
                        checked={isSelected}
                        label={option.label}
                        darkMode={darkMode}
                        disabled={isOptionDisabled}
                        onChange={() => handleToggleOption(option.value)}
                      />
                    </div>
                  )
                })
              )}
            </div>
          </div>
        ) : null}
      </div>

      {resolvedHelperText ? (
        <p
          className={`ds-multiselect-combobox__helper${
            isError ? ' ds-multiselect-combobox__helper--error' : ''
          }`}
        >
          {resolvedHelperText}
        </p>
      ) : null}
    </div>
  )
}

export default MultiselectCombobox
